import { createClient } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";

// Public scoreboards never inherit an expired saved login from the main client.
const publicSupabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY,
  {
    // This client always uses the public API key. Supplying accessToken avoids
    // creating a second GoTrue session manager or sharing the main auth lock.
    accessToken: async () => null,
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
  },
);

// Notifications invalidate the display; scores always come from the shared
// server engine, never from a client-supplied broadcast payload.
export function subscribeTournamentFlow(
  tournamentId,
  onChange,
  authenticated = false,
) {
  const subscribed = (status) => {
    if (status === "SUBSCRIBED") onChange();
  };
  const publicChannel = publicSupabase
    .channel(`tournament-flow:${tournamentId}`, { config: { private: false } })
    .on("broadcast", { event: "updated" }, onChange)
    .subscribe(subscribed);
  const adminChannel = authenticated
    ? supabase
        .channel(`website-tournament-flow:${tournamentId}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "tournament_flow_runs",
            filter: `tournament_id=eq.${tournamentId}`,
          },
          onChange,
        )
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "matchmaking_queue",
            filter: `tournament_id=eq.${tournamentId}`,
          },
          onChange,
        )
        .subscribe(subscribed)
    : null;
  return () => {
    publicSupabase.removeChannel(publicChannel);
    if (adminChannel) supabase.removeChannel(adminChannel);
  };
}

export function validateFlowRun(run) {
  if (
    run &&
    (run.state?.version !== 1 ||
      !Array.isArray(run.state?.matches) ||
      !Array.isArray(run.state?.stages) ||
      !Array.isArray(run.state?.entrants))
  ) {
    throw new Error(
      "This tournament could not be displayed. Refresh or contact the organizer.",
    );
  }
  return run;
}

// All progression, permissions and revision checks remain in the shared server engine.
export async function tournamentFlow(tournamentId, action = "read", args = {}) {
  const { data, error } = await supabase.functions.invoke("tournament-flow", {
    body: { tournament_id: tournamentId, action, ...args },
    timeout: action === "read" ? 15000 : 45000,
  });
  if (error) {
    if (["FunctionsFetchError", "FunctionsRelayError"].includes(error.name)) {
      throw new Error(
        action === "read"
          ? "Could not load the tournament. Check your connection and retry."
          : "The server response was interrupted. Refresh to check whether your change was saved before trying again.",
      );
    }
    let message = error.message;
    try {
      message = (await error.context?.json())?.error || message;
    } catch {
      /* No response body on network failure. */
    }
    throw new Error(
      message || "Could not connect. Check your connection and retry.",
    );
  }
  if (!data || typeof data !== "object" || data.error) {
    throw new Error(
      data?.error ||
        "The tournament response was incomplete. Refresh and try again.",
    );
  }
  validateFlowRun(data.run);
  return data;
}

export async function publicTournamentFlow(tournamentId) {
  const { data, error } = await publicSupabase.rpc(
    "get_public_tournament_flow",
    {
      p_tournament_id: tournamentId,
    },
  );
  if (error)
    throw new Error(
      "Could not refresh the scoreboard. Check your connection and retry.",
    );
  return { run: validateFlowRun(data), is_admin: false };
}

export async function publicTournamentRosters(tournamentId) {
  const { data, error } = await publicSupabase.rpc(
    "get_public_tournament_rosters",
    {
      p_tournament_id: tournamentId,
    },
  );
  if (error)
    throw new Error("Could not load team members. Refresh to try again.");
  return Array.isArray(data) ? data : [];
}
